/**
 * Rift Gate — portal entre regiões (Fase 8). Composição estilizada: arco de pedra,
 * cristais integrados, núcleo energético, runa no chão e partículas discretas.
 * Estado INATIVO (escuro) antes do Apex Fang; ATIVO (energia acesa) depois.
 * É um interagível (o Game trata o USAR). Reposicionável por região.
 *
 * 10.9BS — IDENTIDADE POR REGIÃO. O mesmo mesh servia as 3 regiões; agora a silhueta base é a
 * mesma, mas cada `variant` tem cor de energia e adornos próprios:
 *   forest  · verde-cristal · RAÍZES (cones deitados) abraçando a base dos pilares
 *   wilds   · ciano         · LASCAS de cristal flutuando e girando ao redor do arco
 *   tempest · azul-elétrico · 2 ANÉIS Torus girando em eixos cruzados
 * Os três conjuntos são construídos uma vez e só alternam visibilidade (custo zero na troca).
 */
import * as THREE from "three";
import { crystalStdMat, energyMat, stoneMat, woodMat } from "./Materials";
import { makeVortexMaterial, makePortalSparks, type VortexMaterial, type PortalSparks } from "./PortalVortex";
import { makeLabel, disposeLabel } from "./StructureLabel";

export type RiftVariant = "forest" | "wilds" | "tempest";

/** 10.9BS — cor de energia de cada variante (sobrepõe o accent do tema). */
const VARIANT_COLOR: Record<RiftVariant, number> = {
  forest: 0x7cf0a8,
  wilds: 0x62e8ff,
  tempest: 0x6fb6ff,
};

/** Deriva a variante a partir do id da região (mesma regra usada pelo RegionManager). */
export function riftVariantFor(regionId: string): RiftVariant {
  return regionId === "forgottenForest" ? "forest" : regionId === "tempestReach" ? "tempest" : "wilds";
}

export class RiftGate {
  readonly group = new THREE.Group();
  private active = false;
  private pulse = 0;
  private variant: RiftVariant = "wilds";
  // 10.9BQ — clones da biblioteca (mudam de cor no setAccent e pulsam no update)
  private readonly coreMat: VortexMaterial; // 10.9GU — vórtice em shader
  private readonly core: THREE.Mesh;
  private readonly sparks: PortalSparks;
  private label: THREE.Object3D | null = null;
  private accentHex = "#62e8ff";
  private readonly runeMat: THREE.MeshBasicMaterial;
  private readonly crystalMat: THREE.MeshStandardMaterial;
  /** 10.9BQ — UMA luz só (era intensidade 4): o hub inteiro segue uma escala de luz única. */
  private readonly light: THREE.PointLight;
  // 10.9BS — adornos por variante
  private readonly forestDeco = new THREE.Group();
  private readonly wildsDeco = new THREE.Group();
  private readonly tempestDeco = new THREE.Group();
  private readonly shards: { m: THREE.Mesh; r: number; y: number; ph: number }[] = [];
  private readonly rings: THREE.Mesh[] = [];
  private readonly ringMat: THREE.MeshBasicMaterial;

  constructor(scene: THREE.Scene, accent = 0x62e8ff, variant: RiftVariant = "wilds") {
    const rock = stoneMat(0x44506a);

    // arco de pedra (2 pilares + topo curvo em blocos)
    const legGeo = new THREE.BoxGeometry(0.9, 4.4, 0.9);
    const l1 = new THREE.Mesh(legGeo, rock); l1.position.set(-2.1, 2.2, 0); l1.castShadow = true;
    const l2 = new THREE.Mesh(legGeo, rock); l2.position.set(2.1, 2.2, 0); l2.castShadow = true;
    this.group.add(l1, l2);
    // topo em arco (blocos angulados)
    const topGeo = new THREE.BoxGeometry(1.1, 0.9, 0.9);
    for (const [bx, by, rot] of [[-1.5, 4.4, 0.5], [0, 4.85, 0], [1.5, 4.4, -0.5]]) {
      const b = new THREE.Mesh(topGeo, rock);
      b.position.set(bx, by, 0);
      b.rotation.z = rot;
      b.castShadow = true;
      this.group.add(b);
    }

    // cristais integrados nos pilares
    const cryGeo = new THREE.OctahedronGeometry(0.4, 0);
    this.crystalMat = crystalStdMat(accent).clone(); // um clone pros 5 (pulsam juntos)
    for (const [cx, cy, s] of [[-2.1, 3.4, 1], [2.1, 3.4, 1], [-2.1, 1.4, 0.8], [2.1, 1.4, 0.8], [0, 5.3, 1.2]]) {
      const c = new THREE.Mesh(cryGeo, this.crystalMat);
      c.position.set(cx, cy, 0);
      c.scale.setScalar(s);
      this.group.add(c);
    }

    // núcleo energético (disco entre os pilares)
    this.coreMat = makeVortexMaterial(accent, 0); // 10.9GU
    this.core = new THREE.Mesh(new THREE.CircleGeometry(1.9, 32), this.coreMat);
    this.core.position.set(0, 2.5, 0);
    this.group.add(this.core);
    this.sparks = makePortalSparks(this.group, accent, 2.3, 28, 5.2); // 10.9GU

    // runa no chão
    this.runeMat = energyMat(accent, "ring").clone();
    const rune = new THREE.Mesh(new THREE.RingGeometry(2.0, 2.6, 6), this.runeMat);
    rune.rotation.x = -Math.PI / 2;
    rune.position.y = 0.05;
    this.group.add(rune);

    this.light = new THREE.PointLight(accent, 0, 14, 2);
    this.light.position.set(0, 2.6, 0);
    this.group.add(this.light);

    // ---------------------------------------------------------- 10.9BS adornos
    // FLORESTA — raízes: cones deitados saindo da base de cada pilar, afundando no chão
    const rootGeo = new THREE.ConeGeometry(0.22, 1.9, 5);
    const rootMat = woodMat();
    for (const px of [-2.1, 2.1]) {
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + (px < 0 ? 0.4 : 1.1);
        const r = new THREE.Mesh(rootGeo, rootMat);
        r.position.set(px + Math.cos(a) * 0.95, 0.18, Math.sin(a) * 0.95);
        // deitado, apontando para fora e levemente inclinado para o chão
        r.rotation.set(Math.PI / 2 + 0.16, -a + Math.PI / 2, 0, "YXZ");
        r.scale.setScalar(0.8 + (i % 2) * 0.35);
        this.forestDeco.add(r);
      }
    }
    // TERRAS PARTIDAS — lascas flutuando em órbita lenta ao redor do arco
    const shardGeo = new THREE.OctahedronGeometry(0.22, 0);
    for (let i = 0; i < 7; i++) {
      const m = new THREE.Mesh(shardGeo, this.crystalMat);
      m.scale.set(0.7, 1.9, 0.7);
      this.wildsDeco.add(m);
      this.shards.push({ m, r: 2.9 + (i % 3) * 0.45, y: 1.6 + (i * 0.55) % 3.2, ph: (i / 7) * Math.PI * 2 });
    }
    // TEMPESTADE — 2 anéis Torus energizados girando em eixos cruzados
    this.ringMat = energyMat(accent, "ring").clone();
    const torusGeo = new THREE.TorusGeometry(2.6, 0.07, 6, 40);
    for (let i = 0; i < 2; i++) {
      const ring = new THREE.Mesh(torusGeo, this.ringMat);
      ring.position.set(0, 2.5, 0);
      this.tempestDeco.add(ring);
      this.rings.push(ring);
    }
    this.group.add(this.forestDeco, this.wildsDeco, this.tempestDeco);

    scene.add(this.group);
    this.setVariant(variant);
    this.applyActive();
  }

  setPosition(x: number, z: number): void {
    this.group.position.set(x, 0, z);
  }
  /** 10.9GU — placa do portal (título + destino/nível). Recria só quando o texto muda. */
  setLabel(title: string, sub?: string): void {
    const key = `${title}|${sub ?? ""}|${this.accentHex}`;
    if (this.label && this.label.userData.key === key) return;
    disposeLabel(this.label);
    this.label = makeLabel(title, this.accentHex, 5.7, sub);
    this.label.userData.key = key;
    this.group.add(this.label);
  }
  setAccent(color: number): void {
    this.accentHex = "#" + color.toString(16).padStart(6, "0");
    this.coreMat.uniforms.uColor.value.setHex(color);
    this.sparks.setColor(color);
    this.runeMat.color.setHex(color);
    this.ringMat.color.setHex(color);
    this.light.color.setHex(color);
    this.crystalMat.color.setHex(color);
    this.crystalMat.emissive.setHex(color);
  }
  /** 10.9BS — troca a identidade do portal (cor de energia + adornos visíveis). */
  setVariant(v: RiftVariant): void {
    this.variant = v;
    this.forestDeco.visible = v === "forest";
    this.wildsDeco.visible = v === "wilds";
    this.tempestDeco.visible = v === "tempest";
    this.setAccent(VARIANT_COLOR[v]);
  }
  get currentVariant(): RiftVariant {
    return this.variant;
  }
  setActive(v: boolean): void {
    this.active = v;
    this.applyActive();
  }
  get isActive(): boolean {
    return this.active;
  }
  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  private applyActive(): void {
    this.crystalMat.emissiveIntensity = this.active ? 1.4 : 0.15;
    this.coreMat.uniforms.uOpen.value = this.active ? 1 : 0.12;
    this.sparks.setOn(this.active);
    this.runeMat.opacity = this.active ? 0.45 : 0.12;
    this.ringMat.opacity = this.active ? 0.45 : 0.1;
    this.light.intensity = this.active ? 3 : 0;
  }

  isPlayerNear(pos: { x: number; z: number }, radius: number): boolean {
    const dx = pos.x - this.group.position.x;
    const dz = pos.z - this.group.position.z;
    return dx * dx + dz * dz <= radius * radius;
  }

  update(dt: number): void {
    if (!this.group.visible) return;
    this.pulse += dt;
    const spin = this.active ? 1 : 0.2;
    this.coreMat.uniforms.uTime.value = this.pulse;
    this.sparks.update(dt);
    if (this.active) {
      this.crystalMat.emissiveIntensity = 1.2 + Math.sin(this.pulse * 2.5) * 0.4;
      this.light.intensity = 3 + Math.sin(this.pulse * 3) * 0.6;
    }
    // 10.9BS — adornos animados (só o conjunto visível)
    if (this.variant === "wilds") {
      for (const s of this.shards) {
        const a = this.pulse * 0.35 * spin + s.ph;
        s.m.position.set(Math.cos(a) * s.r, s.y + Math.sin(this.pulse * 1.3 + s.ph) * 0.25, Math.sin(a) * s.r);
        s.m.rotation.y = a * 2;
        s.m.rotation.z = Math.sin(this.pulse * 0.7 + s.ph) * 0.4;
      }
    } else if (this.variant === "tempest") {
      const r0 = this.rings[0], r1 = this.rings[1];
      r0.rotation.set(this.pulse * 0.9 * spin, this.pulse * 0.5 * spin, 0);
      r1.rotation.set(Math.PI / 2 + Math.sin(this.pulse * 0.6) * 0.5, -this.pulse * 0.7 * spin, this.pulse * 0.4 * spin);
    }
  }
}
